import { Component, Input, OnInit, inject } from "@angular/core";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { Store } from "@ngrx/store";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogClose
} from "@angular/material/dialog";
import { UntypedFormBuilder, FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { ResourceStore } from "@app/services/store/resource-store";
import { ResourceModel } from "@app/models/rest/resource";
import { MatToolbar, MatToolbarRow } from "@angular/material/toolbar";
import { MatIconAnchor } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatCheckbox } from "@angular/material/checkbox";
import { GridComponent } from "../base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";
import { TranslatePipe } from "@ngx-translate/core";
import { Store as BryntumStore } from "@bryntum/core-thin";
import { SkillStore } from "@app/services/store/skill-store";
import { ZoneStore } from "@app/services/store/zone-store";

export interface ResourceChooserInputData {
  requiredSkillIds: number[];
  requiredZoneId: number;
  requiredTeamId: number;
}

@Component({
  selector: "app-resource-chooser-dialog",
  templateUrl: "./resource-chooser-dialog.component.html",
  styleUrls: ["./resource-chooser-dialog.component.scss"],
  imports: [
    MatToolbar,
    MatToolbarRow,
    MatIconAnchor,
    MatDialogClose,
    MatIcon,
    MatCheckbox,
    FormsModule,
    GridComponent,
    MatPaginator,
    TranslatePipe
  ]
})
export class ResourceChooserDialogComponent
  extends BaseDataTablePageDirective<ResourceModel>
  implements OnInit
{
  dialogRef =
    inject<MatDialogRef<ResourceChooserDialogComponent>>(MatDialogRef);
  systemSearchMetaRestService: SystemSearchMetaRestService;
  resourceStore: ResourceStore;
  protected store: Store<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;
  data = inject<ResourceChooserInputData>(MAT_DIALOG_DATA);

  private skillStoreService = inject(SkillStore);
  private zoneStoreService = inject(ZoneStore);

  private skillsBryntumStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  private zonesBryntumStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  @Input()
  public columns: object[] = [
    {
      field: "name",
      text: "Name",
      // type: 'template',
      flex: 1,
      template: ({ record }) => `<dl style="margin:0" >
                 <dt><img alt="Resource Image" src="${record.resource.imageUrl}" height="40" width="40" style="border-radius:100%;box-shadow:1px 0 5px #aaa"/></dt>
                 </dl>${record.resource.name}`,
      editor: false
    },
    {
      field: "skillsList[skill.description]",
      text: "Skills",
      type: "template",
      flex: 1,
      template: ({ record }) =>
        `${record.skillsList.map((x) => x.skill.description).join(", ")}`,
      editor: false,
      filterable: {
        filterField: {
          type: "combo",
          multiSelect: true,
          valueField: "id",
          displayField: "name",
          placeholder: "Filter by skills...",
          store: this.skillsBryntumStore
        }
      }
    },
    {
      field: "zonesList[zone.name]",
      text: "Zones",
      type: "template",
      flex: 1,
      template: ({ record }) =>
        `${record.zonesList.map((x) => x.zoneName).join(", ")}`,
      editor: false,
      filterable: {
        filterField: {
          type: "combo",
          multiSelect: true,
          valueField: "id",
          displayField: "name",
          placeholder: "Filter by zones...",
          store: this.zonesBryntumStore
        }
      }
    },
    {
      type: "action",
      text: "",
      width: 50,
      actions: [
        {
          cls: "fa fa-check",
          tooltip: "Pick Resource",
          onClick: ({ record }) => {
            // console.log(`Picking ${record.name}`);
            this.dialogRef.close(record);
          }
        }
      ]
    }
  ];
  hasRequiredSkill: boolean = true;
  hasRequiredZone: boolean = true;

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const resourceStore = inject(ResourceStore);
    const store = inject<Store<any>>(Store);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      resourceStore,
      store,
      dialog,
      "Resource",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );

    this.systemSearchMetaRestService = systemSearchMetaRestService;
    this.resourceStore = resourceStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.gridStore.autoCommit = false;
    this.loadFilterData();
    this.updateRequiredFilters();
  }

  private async loadFilterData() {
    try {
      const [skillsResult, zonesResult] = await Promise.all([
        this.skillStoreService.loadAll({ pageParams: { size: 5000 } }, true),
        this.zoneStoreService.loadAll({ pageParams: { size: 5000 } }, true)
      ]);

      const skillData = (skillsResult.resources || [])
        .filter((s) => s && s.id && s.description)
        .map((s) => ({ id: s.id, name: s.description }));
      const zoneData = (zonesResult.resources || [])
        .filter((z) => z && z.id && z.name)
        .map((z) => ({ id: z.id, name: z.name }));

      this.skillsBryntumStore.removeAll();
      if (skillData.length > 0) {
        this.skillsBryntumStore.add(skillData);
      }

      this.zonesBryntumStore.removeAll();
      if (zoneData.length > 0) {
        this.zonesBryntumStore.add(zoneData);
      }
    } catch (error) {
      console.error("Error loading skill/zone filter data:", error);
    }
  }

  updateRequiredFilters() {
    this.gridStore.fixedFilters = [];
    if (this.data && !!this.data.requiredZoneId && this.hasRequiredZone) {
      this.gridStore.fixedFilters = [
        {
          property: "zonesList[zone.id]",
          operator: "Equals",
          value: this.data.requiredZoneId
        }
      ];
    }
    if (this.data && !!this.data.requiredSkillIds && this.hasRequiredSkill) {
      this.data.requiredSkillIds.forEach((skillId) => {
        this.gridStore.fixedFilters.push({
          property: "skillsList[skill.id]",
          operator: "Equals",
          value: skillId
        });
      });
    }

    this.gridStore.load({});
  }
}
